import React, { useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCallPhase,
  selectIsMuted,
  selectIsSpeakerOn,
  selectIsMicOn,
  selectDemoMode,
  selectMicAvailable,
  selectUserText,
  selectError,
  initiateCall,
  setCompanyInfo,
  setMicAvailable,
  setDemoMode,
  clearError,
  toggleMute,
  toggleSpeaker,
  toggleMic,
} from './redux/slices/callSlice';
import { selectTheme } from './redux/slices/themeSlice';
import useVoiceChat from './hooks/useVoiceChat';
import useAudio from './hooks/useAudio';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import Loader from './components/Skeleton/Skeleton';
import CallButton from './components/CallButton/CallButton';
import CallPopup from './components/CallPopup/CallPopup';

const LandingPage = lazy(() => import('./pages/LandingPage'));

const CompanyRoute = () => {
  const { slug } = useParams();
  const dispatch = useDispatch();
  useEffect(() => {
    if (slug) dispatch(setCompanyInfo({ name: slug, slug }));
  }, [slug, dispatch]);
  return <LandingPage />;
};

function App() {
  const dispatch = useDispatch();
  const theme = useSelector(selectTheme);
  const callPhase = useSelector(selectCallPhase);
  const isMuted = useSelector(selectIsMuted);
  const isSpeakerOn = useSelector(selectIsSpeakerOn);
  const isMicOn = useSelector(selectIsMicOn);
  const demoMode = useSelector(selectDemoMode);
  const micAvailable = useSelector(selectMicAvailable);
  const userText = useSelector(selectUserText);
  const callError = useSelector(selectError);

  const { audioLevel, requestMicrophone, stopStream } = useAudio();
  const { initializeCall, endVoiceCall, handleUserMessage, toggleListening, isAISpeaking } = useVoiceChat();
  const ringtoneRef = useRef(null);
  const toggleListeningRef = useRef(toggleListening);

  useEffect(() => { toggleListeningRef.current = toggleListening; }, [toggleListening]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const startRingtone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ringtoneRef.current = ctx;
      const playBeep = () => {
        if (!ringtoneRef.current) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 440;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      };
      playBeep();
      ringtoneRef.current._interval = setInterval(playBeep, 2000);
    } catch (e) {}
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      if (ringtoneRef.current._interval) {
        clearInterval(ringtoneRef.current._interval);
      }
      ringtoneRef.current.close();
      ringtoneRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (callPhase === 'ringing') {
      startRingtone();
    } else {
      stopRingtone();
    }
  }, [callPhase, startRingtone, stopRingtone]);

  useEffect(() => {
    if (callPhase === 'ringing') {
      const t = setTimeout(async () => {
        let hasMic = false;
        try {
          const stream = await requestMicrophone();
          if (stream && stream.active) {
            hasMic = true;
            dispatch(setMicAvailable(true));
          }
        } catch (err) {
          hasMic = false;
          dispatch(setMicAvailable(false));
          dispatch(setDemoMode(true));
        }
        initializeCall('creato-software', hasMic);
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [callPhase, requestMicrophone, initializeCall]);

  const handleCallButtonClick = useCallback(() => {
    dispatch(clearError());
    dispatch(initiateCall());
  }, [dispatch]);

  const handleToggleMute = useCallback(() => {
    window.speechSynthesis.cancel();
    dispatch(toggleMute());
  }, [dispatch]);

  const handleToggleSpeaker = useCallback(() => {
    window.speechSynthesis.cancel();
    dispatch(toggleSpeaker());
  }, [dispatch]);

  const handleToggleMic = useCallback(() => {
    toggleListeningRef.current();
    dispatch(toggleMic());
  }, [dispatch]);

  const handleEndCall = useCallback(() => {
    stopStream();
    stopRingtone();
    endVoiceCall();
  }, [stopStream, stopRingtone, endVoiceCall]);

  const handleSendText = useCallback((text) => {
    if (handleUserMessage) handleUserMessage(text);
  }, [handleUserMessage]);

  return (
    <ErrorBoundary>
      <div className="App">
        <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}><Loader /></div>}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/:slug" element={<CompanyRoute />} />
          </Routes>
        </Suspense>
        <CallButton onClick={handleCallButtonClick} />
        <CallPopup
          audioLevel={audioLevel}
          isMuted={isMuted}
          isSpeakerOn={isSpeakerOn}
          isMicOn={isMicOn}
          onToggleMute={handleToggleMute}
          onToggleSpeaker={handleToggleSpeaker}
          onToggleMic={handleToggleMic}
          onEndCall={handleEndCall}
          demoMode={demoMode}
          micAvailable={micAvailable}
          onSendText={handleSendText}
          userText={userText}
          callError={callError}
          onClearError={() => dispatch(clearError())}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
